import { motion } from "framer-motion";
import { CheckCircle2, XCircle, MapPin, GraduationCap, Building2, Calendar, ExternalLink, Sparkles, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const STATUS_STYLES = {
  new: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  applied: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  rejected: "bg-red-500/10 text-red-300 border-red-500/30",
};
const STATUS_LABEL = { new: "Nowa", applied: "Aplikowałem", rejected: "Odrzucona" };

function ScoreStars({ score = 0, testid }) {
  // Map raw score (0..~8) → 0..5 stars
  const n = Math.max(0, Math.min(5, Math.round((score / 8) * 5)));
  if (!n) return null;
  return (
    <span className="inline-flex items-center gap-0.5" data-testid={testid} title={`Match score: ${score}`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          className={`w-3 h-3 ${i < n ? "text-cyan-400 fill-cyan-400" : "text-slate-700"}`}
          strokeWidth={1.5}
        />
      ))}
    </span>
  );
}

export default function OfferCard({ offer, onStatusChange }) {
  const s = offer.status || "new";
  const dist = offer.distance_km;
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 260, damping: 20 }}
      data-testid={`offer-card-${offer.id}`}
      className="group relative rounded-xl border border-slate-800 bg-slate-900/60 p-5 hover:border-cyan-400/40 hover:shadow-[0_0_25px_rgba(34,211,238,0.08)]"
    >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={`${STATUS_STYLES[s]} rounded-md border`} data-testid={`offer-status-${offer.id}`}>
                {STATUS_LABEL[s]}
              </Badge>
              {offer.matched && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-cyan-300">
                  <Sparkles className="w-3 h-3" />
                  dopasowana
                </span>
              )}
              <ScoreStars score={offer.match_score} testid={`offer-score-${offer.id}`} />
            </div>
          <h3 className="font-display text-lg font-semibold text-slate-50 leading-tight">{offer.title}</h3>
          {offer.school && (
            <div className="mt-2 flex items-center gap-2 text-sm text-slate-400">
              <Building2 className="w-3.5 h-3.5" />
              <span className="truncate">{offer.school}</span>
            </div>
          )}
        </div>
        {dist != null && (
          <div className="text-right shrink-0">
            <div className="text-2xl font-display font-semibold text-cyan-400 leading-none">{dist}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">km</div>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        {offer.city && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-800/70 text-slate-300">
            <MapPin className="w-3 h-3" /> {offer.city}
          </span>
        )}
        {offer.level && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-800/70 text-slate-300">
            <GraduationCap className="w-3 h-3" /> {offer.level}
          </span>
        )}
        {offer.deadline && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-800/70 text-slate-300">
            <Calendar className="w-3 h-3" /> do {offer.deadline}
          </span>
        )}
      </div>

      {offer.subjects?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {offer.subjects.map((sub) => (
            <span
              key={sub}
              className="text-[11px] px-2 py-0.5 rounded-full bg-cyan-400/10 text-cyan-300 border border-cyan-400/20"
            >
              {sub}
            </span>
          ))}
        </div>
      )}

      {(offer.attachments || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`offer-attachments-${offer.id}`}>
          {offer.attachments.map((a) => (
            <a
              key={a.url}
              href={a.url}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-300 border border-amber-500/20 hover:bg-amber-500/20 inline-flex items-center gap-1"
            >
              PDF · {a.filename?.slice(0, 40)}
            </a>
          ))}
        </div>
      )}

      <div className="mt-5 flex items-center justify-between gap-2 pt-4 border-t border-slate-800">
        <div className="flex gap-2">
          <button
            data-testid={`offer-mark-applied-${offer.id}`}
            onClick={() => onStatusChange?.(offer, s === "applied" ? "new" : "applied")}
            className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors flex items-center gap-1 ${
              s === "applied"
                ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                : "border-slate-700 text-slate-400 hover:border-emerald-500/40 hover:text-emerald-300"
            }`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Aplikowałem
          </button>
          <button
            data-testid={`offer-mark-rejected-${offer.id}`}
            onClick={() => onStatusChange?.(offer, s === "rejected" ? "new" : "rejected")}
            className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors flex items-center gap-1 ${
              s === "rejected"
                ? "bg-red-500/20 border-red-500/40 text-red-300"
                : "border-slate-700 text-slate-400 hover:border-red-500/40 hover:text-red-300"
            }`}
          >
            <XCircle className="w-3.5 h-3.5" /> Odrzuć
          </button>
        </div>
        {offer.source_url && (
          <a
            href={offer.source_url}
            target="_blank"
            rel="noreferrer"
            data-testid={`offer-open-source-${offer.id}`}
            className="text-xs text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1"
          >
            Źródło <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </motion.div>
  );
}
